import { FirestoreDataConverter } from '@angular/fire/firestore';
import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { DocumentModel, DocumentModelForm } from "./document.model";

export class FooterSection {
  name = '';
  position = 1;
  html = '';
  level = 1;
  cols = 1
}

export class HeaderSection {
  name = '';
  title = '';
  subtitle = '';
  textColor = '';
  textAlign: 'left' | 'center' | 'right' = 'center';
  position = 1;
  link = '';
  linkText = '';
  imageUrl = '';
}

export type FooterSectionForm = FormGroup<{
  name: FormControl<string>,
  position: FormControl<number>,
  html: FormControl<string>,
  level: FormControl<number>,
  cols: FormControl<number>,
}>

export type HeaderSectionForm = FormGroup<{
  name: FormControl<string>,
  title: FormControl<string>,
  position: FormControl<number>,
  imageUrl: FormControl<string>,
  link: FormControl<string>,
  linkText: FormControl<string>,
  subtitle: FormControl<string>,
  textColor: FormControl<string>,
  textAlign: FormControl<'left' | 'center' | 'right'>,
}>

export type CommerceShopForm = FormGroup<{
  id: FormControl<string>,
  name: FormControl<string>,
  description: FormControl<string>,
  logo: FormControl<string>,
  banner: FormControl<string>,
  template: FormControl<string>,
  url: FormControl<string>,
  allowedEmails: FormControl<string[]>,
  public: FormControl<boolean>,
  customer: FormControl<string>,
  favIcon: FormControl<string>,
  promotionalText: FormControl<string>,
  primaryColor: FormControl<string>,
  batPrice: FormControl<number>,
  shippingMargin: FormControl<number>,
  quotationsGeneration: FormControl<boolean>,
  contact: FormGroup<{
    email: FormControl<string>,
    phone: FormControl<string>
  }>,
  socials: FormGroup<{
    facebook: FormControl<string>,
    instagram: FormControl<string>,
    twitter: FormControl<string>,
    youtube: FormControl<string>
    linkedin: FormControl<string>,
  }>,
  facebookPixelScript: FormControl<string>,
  googleAdsScript: FormControl<string>,
  organizationId: FormControl<string>,
  salesManagerId: FormControl<string>,
  askInvoicingAddress: FormControl<boolean>,
  footerSections: FormArray<FooterSectionForm>;
  headerSections: FormArray<HeaderSectionForm>;
  images: FormArray<DocumentModelForm>;
  appKey: FormControl<string>,
  domain: FormControl<string>,
  simulatorIsActive: FormControl<boolean>,
  simulatorImageUrl: FormControl<string>,
  comparatorBeforeImageUrl: FormControl<string>,
  comparatorAfterImageUrl: FormControl<string>,
  comparatorIsActive: FormControl<boolean>,
  multiCustomer: FormControl<boolean>,
  customSmtp: FormControl<boolean>,
}>

/**
 * Model for Commerce Shop (Used for Commerce)
 */
export class CommerceShop {
  id: string;
  name: string;
  description: string;
  logo: string;
  banner: string;
  template: string;
  url: string;
  allowedEmails: string[];
  public: boolean;
  customer: string;
  primaryColor: string;
  promotionalText: string;
  favIcon: string;
  batPrice: number;
  shippingMargin: number;
  quotationsGeneration: boolean;
  contact: {email: string, phone: string};
  socials: {
    facebook: string,
    instagram: string,
    twitter: string,
    youtube: string,
    linkedin: string
  };
  facebookPixelScript: string;
  googleAdsScript: string;
  organizationId: string;
  salesManagerId: string;
  askInvoicingAddress: boolean;
  footerSections: FooterSection[];
  headerSections: HeaderSection[];
  images: DocumentModel[];
  domain: string;
  appKey: string;
  simulatorIsActive: boolean;
  simulatorImageUrl: string;
  comparatorBeforeImageUrl: string;
  comparatorAfterImageUrl: string;
  comparatorIsActive: boolean;
  multiCustomer: boolean; // User is allowed to choose their customer account
  customSmtp: boolean; // Emails are sent via custom SMTP server

  constructor(data?: Partial<CommerceShop>) {
    this.id = data?.id ?? '';
    this.name = data?.name ?? '';
    this.description = data?.description ?? '';
    this.logo = data?.logo ?? '';
    this.banner = data?.banner ?? '';
    this.template = data?.template ?? '';
    this.url = data?.url ?? '';
    this.allowedEmails = data?.allowedEmails ?? [];
    this.public = data?.public ?? false;
    this.customer = data?.customer ?? '';
    this.primaryColor = data?.primaryColor ?? '';
    this.promotionalText = data?.promotionalText ?? '';
    this.favIcon = data?.favIcon ?? '';
    this.batPrice = data?.batPrice ?? 0;
    this.shippingMargin = data?.shippingMargin ?? 0;
    this.quotationsGeneration = data?.quotationsGeneration ?? false;
    this.contact = {
      email: data?.contact?.email ?? '',
      phone: data?.contact?.phone ?? '',
    };
    this.socials = {
      facebook: data?.socials?.facebook ?? '',
      instagram: data?.socials?.instagram ?? '',
      twitter: data?.socials?.twitter ?? '',
      youtube: data?.socials?.youtube ?? '',
      linkedin: data?.socials?.linkedin ?? '',
    };
    this.facebookPixelScript = data?.facebookPixelScript ?? '';
    this.googleAdsScript = data?.googleAdsScript ?? '';
    this.organizationId = data?.organizationId ?? '';
    this.salesManagerId = data?.salesManagerId ?? '';
    this.askInvoicingAddress = data?.askInvoicingAddress ?? true;
    this.footerSections = data?.footerSections ?? [];
    this.headerSections = data?.headerSections ?? [];
    this.images = data?.images ?? [];
    this.domain = data?.domain ?? '';
    this.appKey = data?.appKey ?? '';
    this.simulatorIsActive = data?.simulatorIsActive ?? false;
    this.simulatorImageUrl = data?.simulatorImageUrl ?? '';
    this.comparatorBeforeImageUrl = data?.comparatorBeforeImageUrl ?? '';
    this.comparatorAfterImageUrl = data?.comparatorAfterImageUrl ?? '';
    this.comparatorIsActive = data?.comparatorIsActive ?? false;
    this.multiCustomer = data?.multiCustomer ?? false;
    this.customSmtp = data?.customSmtp ?? false;
  }

  /**
   * Converter used to convert a shipment from firebase
   */
  static converter: FirestoreDataConverter<CommerceShop> = {
    toFirestore: (shop: CommerceShop) => {
      return {
        id: shop.id,
        name: shop.name.trim(),
        description: shop.description,
        logo: shop.logo,
        banner: shop.banner,
        template: shop.template,
        url: shop.url,
        allowedEmails: shop.allowedEmails,
        public: shop.public,
        customer: shop.customer,
        primaryColor: shop.primaryColor,
        promotionalText: shop.promotionalText,
        favIcon: shop.favIcon,
        batPrice: shop.batPrice,
        shippingMargin: shop.shippingMargin,
        quotationsGeneration: shop.quotationsGeneration,
        contact: shop.contact,
        socials: shop.socials,
        facebookPixelScript: shop.facebookPixelScript,
        googleAdsScript: shop.googleAdsScript,
        organizationId: shop.organizationId,
        salesManagerId: shop.salesManagerId,
        askInvoicingAddress: shop.askInvoicingAddress,
        footerSections: shop.footerSections,
        headerSections: shop.headerSections,
        images: shop.images,
        domain: shop.domain,
        appKey: shop.appKey,
        simulatorIsActive: shop.simulatorIsActive,
        simulatorImageUrl: shop.simulatorImageUrl,
        comparatorBeforeImageUrl: shop.comparatorBeforeImageUrl,
        comparatorAfterImageUrl: shop.comparatorAfterImageUrl,
        comparatorIsActive: shop.comparatorIsActive,
        multiCustomer: shop.multiCustomer,
        customSmtp: shop.customSmtp,
      };
    },
    fromFirestore: (snapshot, options) => {
      return new CommerceShop({
        ...snapshot.data(options),
        id: snapshot.id,
      });
    }
  };
}
